// Optional V3 side-lane result. It is identity metadata, never answer evidence.
const publicText = (value, limit) => {
    if (typeof value !== 'string') return null;
    const text = Array.from(value).map((character) => {
        const code = character.codePointAt(0);
        return code < 32 || code === 127 ? ' ' : character;
    }).join('').replace(/\s+/g, ' ').trim();
    return text && Array.from(text).length <= limit ? text : null;
};

const webURL = (value) => {
    const text = publicText(value, 2048);
    if (!text) return null;
    try {
        const url = new URL(text);
        return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
    } catch {
        return null;
    }
};

const identity = (value) => typeof value === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(value) ? value : null;

const normalizeShow = (show) => {
    const name = publicText(show?.name, 200);
    const creator = publicText(show?.creator, 160);
    const rssURL = webURL(show?.rssURL);
    const appleCollectionId = typeof show?.appleCollectionId === 'string' && /^\d{1,20}$/.test(show.appleCollectionId)
        ? show.appleCollectionId : null;
    if (!name || !creator || !rssURL || !appleCollectionId) return null;
    return { name, creator, rssURL, appleCollectionId, imageURL: webURL(show.imageURL) || '' };
};

export const normalizeEarlyShowMatch = (value) => {
    if (!value || value.schemaVersion !== 2 || !identity(value.runId) ||
        !['possible', 'ambiguous'].includes(value.matchStatus) ||
        !['title', 'title_prefix', 'title_name', 'title_recall'].includes(value.matchKind) ||
        !Array.isArray(value.shows) || value.shows.length < 1 || value.shows.length > 6) return null;
    const byAppleId = new Map();
    const feedOwners = new Map();
    for (const rawShow of value.shows) {
        const show = normalizeShow(rawShow);
        if (!show) return null;
        const previous = byAppleId.get(show.appleCollectionId);
        if (previous && (previous.rssURL !== show.rssURL || previous.name !== show.name || previous.creator !== show.creator)) return null;
        const feedOwner = feedOwners.get(show.rssURL);
        if (feedOwner && feedOwner !== show.appleCollectionId) return null;
        if (!previous) byAppleId.set(show.appleCollectionId, show);
        feedOwners.set(show.rssURL, show.appleCollectionId);
    }
    const shows = [...byAppleId.values()];
    if (value.matchStatus === 'possible' ? shows.length !== 1 : shows.length < 2) return null;
    return { schemaVersion: 2, runId: value.runId, matchStatus: value.matchStatus, matchKind: value.matchKind, shows };
};

export const earlyShowCandidates = (match) => (normalizeEarlyShowMatch(match)?.shows || [])
    .map((show) => ({ showType: 'unindexed', show }));

// Only real, navigable podcast cards can replace a suggestion. Prose and malformed
// card payloads must never make an available show disappear.
export const finalPodcastShows = (sections) => (Array.isArray(sections) ? sections : [])
    .flatMap((section) => Array.isArray(section?.components) ? section.components : [])
    .filter((component) => component?.type === 'podcast')
    .map((component) => component.payload)
    .filter((payload) => payload?.show && (
        (payload.showType === 'indexed' && identity(payload.show.id)) ||
        (payload.showType === 'unindexed' && webURL(payload.show.rssURL))
    ))
    .map((payload) => payload.show);

// Final-show metadata must come from actual rendered podcast cards, not prose
// mentions or title similarity. Different native IDs never collapse by name.
export const sameEarlyShowIdentity = (left, right) => {
    if (!left || !right) return false;
    const appleId = (show) => /^\d{1,20}$/.test(String(show.appleCollectionId || ''))
        ? String(show.appleCollectionId) : null;
    const leftApple = appleId(left), rightApple = appleId(right);
    if (leftApple && rightApple) return leftApple === rightApple;
    const leftId = identity(left.id), rightId = identity(right.id);
    if (leftId && rightId) return leftId === rightId;
    const leftFeed = webURL(left.rssURL), rightFeed = webURL(right.rssURL);
    return Boolean(leftFeed && rightFeed && leftFeed === rightFeed);
};

// Opening an unindexed match uses the existing ingestion-on-click route.
export const earlyShowDestination = (candidate) => {
    if (candidate?.showType === 'indexed' && identity(candidate.show?.id)) {
        return { pathname: `/podcast/${candidate.show.id}` };
    }
    const show = candidate?.showType === 'unindexed' ? normalizeShow(candidate.show) : null;
    if (!show) return null;
    return { pathname: '/load_podcast', state: {
        title: show.name, imageSrc: show.imageURL, rssURL: show.rssURL,
        appleCollectionId: show.appleCollectionId,
    } };
};
